/**
 * Subnet masks, as an operator picks them.
 *
 * A mask is one of about ten values in practice, and typing it is an invitation
 * to typo something a CPE will accept and quietly break: `255.255.255.0` and
 * `255.255.225.0` differ by one keystroke and one of them routes. Offering the
 * list instead makes the wrong answer unreachable.
 *
 * Both notations are shown together because operators use both — a subscriber
 * subnet gets called "a /24" in conversation and written `255.255.255.0` in a
 * config, and pairing them saves the reader converting in their head.
 */
export interface SubnetMask {
  /** Dotted-quad, which is what every CPE in this fleet is written with. */
  mask: string;
  /** Prefix length, which is what people say out loud. */
  prefix: number;
  /** `255.255.255.0 (/24)` — the label a picker shows. */
  label: string;
}

/**
 * The two an operator reaches for, then everything else narrowest first.
 *
 * /24 is a subscriber's LAN and /16 is this fleet's management network, and
 * between them they are almost every mask anybody picks here. In a plain
 * ordering they sit seventh and fifteenth, which means scrolling past thirteen
 * masks nobody uses to reach the two everybody does.
 */
const COMMON = [24, 16];
const REST = [30, 29, 28, 27, 26, 25, 23, 22, 21, 20, 19, 18, 17, 15, 14, 13, 12, 11, 10, 9, 8];

/**
 * From /8 to /30. Narrower than /30 leaves no usable host addresses, and wider
 * than /8 is not something a subscriber or an ONU's LAN is ever given.
 */
export const SUBNET_MASKS: SubnetMask[] = [...COMMON, ...REST].map((prefix) => {
  const mask = maskFromPrefix(prefix);
  return { mask, prefix, label: `${mask} (/${prefix})` };
});

/** `24` → `255.255.255.0`. */
export function maskFromPrefix(prefix: number): string {
  const bits = 0xffffffff << (32 - prefix);
  return [24, 16, 8, 0].map((shift) => ((bits >>> shift) & 0xff).toString()).join('.');
}

/**
 * `255.255.255.0` → `24`, and null for anything that is not a mask.
 *
 * A mask is a run of ones followed by a run of zeroes, so `255.255.225.0` — a
 * plausible typo — is not one, and saying so is more useful than converting it
 * to a number that means nothing.
 */
export function prefixFromMask(mask: string): number | null {
  const parts = mask.trim().split('.');
  if (parts.length !== 4) return null;
  let bits = 0;
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part)) return null;
    const octet = Number(part);
    if (octet > 255) return null;
    bits = (bits << 8) | octet;
  }
  const value = bits >>> 0;
  // Ones followed by zeroes: inverting gives a value one less than a power of
  // two exactly when the mask is contiguous.
  const inverted = ~value >>> 0;
  if (((inverted + 1) & inverted) !== 0) return null;
  let prefix = 0;
  for (let i = 31; i >= 0; i -= 1) {
    if ((value & (1 << i)) === 0) break;
    prefix += 1;
  }
  return prefix;
}

/**
 * How a mask should read wherever one is shown.
 *
 * Anything that is not a mask is returned untouched rather than hidden — a CPE
 * reporting something unexpected is worth seeing, not worth swallowing.
 */
export function subnetLabel(mask: string | null | undefined): string | null {
  if (!mask) return null;
  const prefix = prefixFromMask(mask);
  return prefix === null ? mask : `${mask} (/${prefix})`;
}
